// Batches remote Yjs changes so a burst of peer ops costs ONE React commit
// instead of one synchronous flushSync render per op. A fast-typing peer (or a
// reconnect catch-up sync) can deliver dozens of ops in quick succession;
// applying each one synchronously inside DOMWebSocket.onmessage was measured
// blocking the main thread for seconds (Sentry long-animation-frame, avg 5.8s
// among >2s frames) — the "editor freezes during co-editing" reports.
//
// Coalescing rules:
// - edits dedupe by entityId (the applier reads the CURRENT doc state at flush
//   time, so intermediate values collapse naturally),
// - any structural change supersedes queued edits — the whole-tree reconcile
//   rebuilds every entity anyway,
// - version-data keys are kept separately: version.data is NOT covered by the
//   reconcile snapshot.

export type RemoteApplyEvent =
  | { kind: "edit"; entityId: string }
  | { kind: "version-data"; key: string }
  | { kind: "structural" };

export type RemoteApplyFlush = {
  structural: boolean;
  editIds: string[];
  versionDataKeys: string[];
  ops: number;
};

export type RemoteApplyMetrics = {
  ms: number;
  ops: number;
  structural: boolean;
};

type Schedule = (cb: () => void) => void;

// requestAnimationFrame is paused in hidden tabs, which would hold remote ops
// hostage until the user returns — the setTimeout backstop keeps the doc and
// Redux converging in the background. run() is idempotent so whichever fires
// first wins and the loser is a no-op.
const defaultSchedule: Schedule = (cb) => {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cb();
  };
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
  setTimeout(run, 100);
};

export const createRemoteApplyQueue = (options: {
  apply: (flush: RemoteApplyFlush) => void;
  schedule?: Schedule;
  now?: () => number;
  onMetrics?: (metrics: RemoteApplyMetrics) => void;
}) => {
  const {
    apply,
    schedule = defaultSchedule,
    now = () => performance.now(),
    onMetrics,
  } = options;

  let disposed = false;
  let scheduled = false;
  let structural = false;
  let ops = 0;
  let editIds: string[] = [];
  const editIdSet = new Set<string>();
  let versionDataKeys: string[] = [];
  const versionDataKeySet = new Set<string>();

  const drain = (): RemoteApplyFlush | null => {
    if (ops === 0) return null;
    const flush: RemoteApplyFlush = {
      structural,
      editIds,
      versionDataKeys,
      ops,
    };
    structural = false;
    ops = 0;
    editIds = [];
    editIdSet.clear();
    versionDataKeys = [];
    versionDataKeySet.clear();
    return flush;
  };

  const flush = () => {
    scheduled = false;
    if (disposed) return;
    const batch = drain();
    if (!batch) return;
    const start = now();
    try {
      apply(batch);
    } finally {
      onMetrics?.({ ms: now() - start, ops: batch.ops, structural: batch.structural });
    }
  };

  return {
    enqueue(event: RemoteApplyEvent) {
      if (disposed) return;
      ops += 1;
      if (event.kind === "structural") {
        structural = true;
        editIds = [];
        editIdSet.clear();
      } else if (event.kind === "edit") {
        // Once a reconcile is pending it covers every entity — don't resurrect
        // per-entity edits behind it.
        if (!structural && !editIdSet.has(event.entityId)) {
          editIdSet.add(event.entityId);
          editIds.push(event.entityId);
        }
      } else if (!versionDataKeySet.has(event.key)) {
        versionDataKeySet.add(event.key);
        versionDataKeys.push(event.key);
      }
      if (!scheduled) {
        scheduled = true;
        schedule(flush);
      }
    },
    // Synchronous drain for callers that need the apply to have happened before
    // they continue — collab undo/redo restores the pre-undo selection in a
    // microtask that MUST run after the reconcile.
    flushNow() {
      flush();
    },
    dispose() {
      disposed = true;
    },
  };
};
